// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: how a starting point looks in the getting-started surfaces (Home card, DM starter
// cards, nudge card): its icon, and the brand logos of what it reads.

import {type StarterConnection, starterUses} from '@app/features/konvo_panel/state/starters';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {OutlookCalendarLogo} from '@app/features/ui/components/icons/OutlookCalendarLogo';
import {OutlookLogo} from '@app/features/ui/components/icons/OutlookLogo';
import {WhatsAppLogo} from '@app/features/ui/components/icons/WhatsAppLogo';
import {
	BuildingsIcon,
	CalendarDotsIcon,
	ChatCircleTextIcon,
	CheckSquareIcon,
	ClockIcon,
	ListChecksIcon,
	PencilSimpleIcon,
} from '@app/features/ui/icons/Icons';
import type {Starter} from '@fluxer/schema/src/domains/konvo/AssistantEngagement';
import type React from 'react';

export function StarterIcon({starter, size = 16}: {starter: Starter; size?: number}): React.ReactElement {
	switch (starter) {
		case 'meeting_prep':
			return <CalendarDotsIcon size={size} />;
		case 'client_needs':
			return <BuildingsIcon size={size} />;
		case 'outstanding_work':
			return <ListChecksIcon size={size} />;
		case 'review_time':
			return <ClockIcon size={size} />;
		case 'task_from_email':
			return <CheckSquareIcon size={size} />;
		case 'draft_reply':
			return <PencilSimpleIcon size={size} />;
		case 'free':
			return <ChatCircleTextIcon size={size} />;
	}
}

const BRANDED: ReadonlyArray<StarterConnection> = ['calendar', 'mail', 'clickup', 'clickup_time', 'whatsapp'];

/** Up to three brand logos for what the starter reads (ClickUp time counts as ClickUp). */
export function StarterLogos({starter, size = 14}: {starter: Starter; size?: number}): React.ReactElement | null {
	const seen = new Set<string>();
	const logos: Array<React.ReactElement> = [];
	for (const connection of starterUses(starter)) {
		if (!BRANDED.includes(connection)) continue;
		const key = connection === 'clickup_time' ? 'clickup' : connection;
		if (seen.has(key) || logos.length >= 3) continue;
		seen.add(key);
		if (key === 'calendar') logos.push(<OutlookCalendarLogo key={key} size={size} />);
		else if (key === 'mail') logos.push(<OutlookLogo key={key} size={size} />);
		else if (key === 'clickup') logos.push(<ClickUpLogo key={key} size={size} />);
		else if (key === 'whatsapp') logos.push(<WhatsAppLogo key={key} size={size} />);
	}
	if (logos.length === 0) return null;
	return <>{logos}</>;
}
